import { Box, Modal } from "@mui/material";
import ReactPlayer from "react-player";
import CopyToClipboard from "react-copy-to-clipboard";
import { ButtonV1 } from "../atoms/button";
import { Close, FileDownload, Link } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { PdfViewerModal } from "./PdfViewerModal";
import { ImageGalleryModal } from "./ImageGalleryModal";
import { CreativeType } from "../../types/types";
import { getContentOrientation, ContentOrientation, mapDatabaseOrientation } from "../../utils/orientationUtils";

interface props {
  open: boolean;
  handleClose: VoidFunction;
  item: CreativeType
}

export function VideoPopup({ handleClose, item, open }: props) {
  const [copy, setCopy] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [orientation, setOrientation] = useState<ContentOrientation>(
    mapDatabaseOrientation(item.orientation)
  );

  // Determine media type - default to video if not specified
  const mediaType = item.mediaType || "video";

  // Check content orientation when component mounts or item changes
  useEffect(() => {
    if (item && open) {
      // Synchronize state with item orientation immediately on open or item change
      setOrientation(mapDatabaseOrientation(item.orientation));

      const contentUrl = mediaType === "video" ? item.videoLink :
        mediaType === "image" && item.imageGallery && item.imageGallery.length > 0
          ? item.imageGallery[0]
          : null;

      if (contentUrl) {
        getContentOrientation(contentUrl, mediaType as 'video' | 'image')
          .then((detected) => {
            // Only update if detected it's different and not unknown
            if (detected !== 'unknown') {
              setOrientation(detected);
            }
          })
          .catch(() => {
            // Keep existing orientation if detection fails
          });
      }
    }
  }, [open, item._id, item.orientation, mediaType]);

  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "70vw",
    bgcolor: "background.paper",
    border: "none",
    boxShadow: 4,
    p: 4,
    outline: "none"
  };

  const newTitle = encodeURI(item?.title).replaceAll("+", "%2B");

  const handleClick = (name: string) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_${name}`,
        category: "User Interaction",
        action: "Button Click",
        label: `${name} Button`,
      });
    }
  };

  const handleModalClose = () => {
    setCurrentImageIndex(0);
    setShowPdfViewer(false);
    handleClose();
  };

  return (
    item.mediaType === "ppt" ?
      (item?.pptDetails?.pdfLink && item.pptDetails.pdfLink.trim() !== "") ? (
        <PdfViewerModal
          open={open}
          onClose={handleModalClose}
          item={item}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-200">
          <p className="text-gray-500 text-sm">No PDF available</p>
        </div>
      ) : item.mediaType === "image" ? (
        <ImageGalleryModal
          open={open}
          onClose={handleModalClose}
          item={
            {
              stage: item.stage,
              title: item.title,
              newTitle: item?.newTitle,
              thumbLink: item.fileName || "",
              dt: item.dt,
              orientation: item.orientation,
              imageGallery: item.imageGallery,
              creativeId: item._id
            }
          }
        />
      ) : (
        <Modal
          open={open}
          onClose={handleModalClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          {
            orientation !== "landscape" ? (
              <Box sx={style} className="!w-[95vw] !max-w-[95vw] !h-[90vh] md:!h-[95vh] md:!w-[75vw] md:!max-w-[75vw] lg:!w-[50vw] lg:!max-w-[50vw] !p-0 overflow-hidden md:rounded-3xl">
                <div className="w-full h-full flex flex-col gap-0 relative">
                  <div
                    className="absolute right-1 top-1 cursor-pointer p-1.5 z-50 rounded-3xl bg-gray-500/80 hover:bg-gray-800"
                    onClick={handleModalClose}
                  >
                    <Close style={{ fontSize: "2rem" }} className="text-white cursor-pointer" />
                  </div>
                  <div className="w-full flex-1 flex gap-0 flex-col md:flex-row min-h-0 bg-white">
                    <div className="md:w-[60%] h-full overflow-hidden relative flex-1 min-h-0 bg-white">
                      {(() => {
                        // Video Content
                        if (mediaType === "video") {
                          return (
                            <ReactPlayer
                              url={item.videoLink}
                              playing
                              controls
                              className="h-full w-full min-w-full overflow-hidden outline-none"
                              width="100%"
                              height="100%"
                            />
                          );
                        }

                        // Fallback for no content
                        return (
                          <div className="h-full w-full flex items-center justify-center bg-gray-100">
                            <div className="text-center">
                              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Content Available</h3>
                              <p className="text-gray-600">This {mediaType} content is not available.</p>
                            </div>
                          </div>
                        );
                      })()}

                    </div>
                    <div className="w-full md:w-[40%] h-auto self-stretch flex flex-row md:flex-col gap-2 p-3 md:py-4 md:pt-8 md:px-2 justify-between items-start bg-white md:bg-gray-100 rounded-3xl md:rounded-none md:mt-0 md:mb-0 mx-auto md:mx-0 relative z-20 flex-shrink-0">
                      <div className="w-full h-max flex flex-col gap-0 px-4 py-2 text-xs md:text-lg">
                        <div className="flex items-center gap-1 font-medium text-sm text-gray-700">
                          <span
                            className={`w-2 h-2 rounded-full ${item.stage === "postsale" ? "bg-black" : "bg-gray-500"
                              }`}
                          ></span>
                          {item.stage}
                          {item.dt ? ` | ${item.dt}` : ""}
                        </div>
                        <h1 className="max-w-3/4 text-wrap truncate text-xl font-medium">{item.title}</h1>
                      </div>

                      <div className="flex md:gap-4 relative px-4">
                        <ButtonV1
                          className="scale-75 md:scale-100">
                          <CopyToClipboard
                            text={
                              mediaType === "video"
                                ? `${window.location.origin}/videoPlayer?videoUrl=${encodeURI(item?.videoLink)}&stage=${encodeURIComponent(item.stage || "")}&dtLevel=${encodeURIComponent(item?.dt || "")}&videoName=${encodeURIComponent(newTitle)}`
                                : mediaType === "image"
                                  ? `${window.location.origin}/imageGallery?creativeId=${item._id}`
                                  : mediaType === "ppt"
                                    ? `${window.location.origin}/presentation?creativeId=${item._id}`
                                    : "content"
                            }
                            onCopy={() => {
                              handleClick("CopyLink");
                              setCopy(true);
                            }}
                          >
                            {copy ? (
                              <p className="capitalize">copied</p>
                            ) : (
                              <Link className="text-white" />
                            )}
                          </CopyToClipboard>
                        </ButtonV1>
                        <ButtonV1
                          className="scale-75 md:scale-100">
                          <a
                            href={
                              mediaType === "video"
                                ? item.videoLink
                                : mediaType === "image" && item.imageGallery && item.imageGallery.length > 0
                                  ? item.imageGallery[currentImageIndex]
                                  : mediaType === "ppt" && item.pptDetails?.pdfLink
                                    ? item.pptDetails.pdfLink
                                    : "#"
                            }
                            download={
                              mediaType === "video"
                                ? item.title
                                : mediaType === "image"
                                  ? `${item.title}_image_${currentImageIndex + 1}`
                                  : mediaType === "ppt"
                                    ? `${item.title}_presentation`
                                    : item.title
                            }
                            onClick={() => handleClick("Download")}
                            id="mediaPlayer"
                          >
                            <FileDownload className="text-white" />
                          </a>
                        </ButtonV1>
                      </div>
                    </div>
                  </div>
                </div>
              </Box>
            ) : (
              <Box sx={style} borderRadius={"1.5rem"} className="!w-[100vw] lg:!w-[75vw] !h-[90vh] lg:!max-h-[95vh] !p-0 overflow-hidden md:rounded-3xl">
                <div className="w-full h-full bg-white flex flex-col relative">
                  <div
                    className="absolute right-1 top-1 cursor-pointer p-1.5 z-50 rounded-3xl bg-gray-500/80 hover:bg-gray-800"
                    onClick={handleModalClose}
                  >
                    <Close style={{ fontSize: "2rem" }} className="text-white cursor-pointer" />
                  </div>
                  <div className="w-[100%] flex-1 overflow-hidden relative flex items-center justify-center min-h-0 bg-white">
                    {(() => {
                      // Video Content
                      if (mediaType === "video") {
                        return (
                          <ReactPlayer
                            url={item.videoLink}
                            playing
                            controls
                            className="h-full w-full min-w-full overflow-hidden outline-none mx-auto mt-[0px]"
                            width="100%"
                            height="100%"
                          />
                        );
                      }

                      // Fallback for no content
                      return (
                        <div className="h-full w-full flex items-center justify-center bg-gray-100">
                          <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                              <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">No Content Available</h3>
                            <p className="text-gray-600">This {mediaType} content is not available.</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  {/* Mobile: Floating white card, Desktop: Regular footer */}
                  <div className="w-[92%] md:w-full h-max flex gap-2 justify-between items-center bg-white md:bg-gray-100 rounded-3xl md:rounded-none mx-auto md:mx-0 -mt-6 md:mt-0 mb-3 md:mb-0 p-3 md:p-4 relative md:relative z-20 flex-shrink-0">
                    {/* Left Section - Status Indicator and Text */}
                    <div className="flex items-start gap-2 md:gap-3 flex-1">
                      <div className="w-full h-max flex flex-col gap-0 px-4 py-2 text-xs md:text-lg">
                        <div className="flex items-center gap-1 font-medium text-sm text-gray-700">
                          <span
                            className={`w-2 h-2 rounded-full ${item.stage === "postsale" ? "bg-black" : "bg-gray-500"
                              }`}
                          ></span>
                          {item.stage}
                          {item.dt ? ` | ${item.dt}` : ""}
                        </div>
                        <h1 className="max-w-3/4 text-wrap truncate text-xl font-medium">{item.title}</h1>
                      </div>
                    </div>

                    {/* Right Section - Action Buttons */}
                    <div className="flex gap-2 flex-shrink-0">
                      <ButtonV1>
                        <CopyToClipboard
                          text={
                            mediaType === "video"
                              ? `${window.location.origin}/videoPlayer?videoUrl=${encodeURI(item?.videoLink)}&stage=${encodeURIComponent(item.stage || "")}&dtLevel=${encodeURIComponent(item?.dt || "")}&videoName=${encodeURIComponent(newTitle)}`
                              : mediaType === "image"
                                ? `${window.location.origin}/imageGallery?creativeId=${item._id}`
                                : mediaType === "ppt"
                                  ? `${window.location.origin}/presentation?creativeId=${item._id}`
                                  : "content"
                          }
                          onCopy={() => {
                            handleClick("CopyLink");
                            setCopy(true);
                          }}
                        >
                          {copy ? (
                            <p className="capitalize">copied</p>
                          ) : (
                            <Link className="text-white" />
                          )}
                        </CopyToClipboard>
                      </ButtonV1>

                      <ButtonV1
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = mediaType === "video"
                            ? item.videoLink
                            : mediaType === "image" && item.imageGallery && item.imageGallery.length > 0
                              ? item.imageGallery[currentImageIndex]
                              : mediaType === "ppt" && item.pptDetails?.pdfLink
                                ? item.pptDetails.pdfLink
                                : "#";
                          link.download = mediaType === "video"
                            ? item.title
                            : mediaType === "image"
                              ? `${item.title}_image_${currentImageIndex + 1}`
                              : mediaType === "ppt"
                                ? `${item.title}_presentation`
                                : item.title;
                          link.click();
                          handleClick("Download");
                        }}>
                        <FileDownload className="text-white" />
                      </ButtonV1>
                    </div>
                  </div>
                </div>
              </Box>
            )
          }
        </Modal >
      )
  );
}